// 빠른 설문지의 순수 헬퍼(렌더·상태와 무관). QuickSurveySheet·useQuickSurvey·QuestionCard가 공유한다.

import type { ChecklistField, ChecklistItem, SurveyAnswer } from '~/api/types'

/** 필드가 본문 없는 항목일 때 쓰는 의사(가상) 필드 키. */
export function itemFieldKey(item: ChecklistItem): string {
    return `item:${item.id}`
}

/** 답변 컨트롤 유형 — 예/아니오 칩 · 숫자 입력 · 자유 텍스트. */
export type FieldInput = 'yesno' | 'number' | 'text'

/**
 * 필드에 맞는 답변 컨트롤을 고른다.
 *
 * 중요: 데이터유형 '정성'은 "예/아니오"가 아니라 "수치가 아닌 데이터"라는 뜻이다.
 * 빈도(회)·온도(℃)·서술형도 모두 정성일 수 있으므로 dataType으로 여부형을 판정하지
 * 않는다. 여부형은 단위가 '여부'이거나 라벨에 '(여부)'가 있을 때만이다.
 * - 여부형        → 예/아니오 칩
 * - 그 외 단위 有 → 숫자 입력(회·℃·% 등 계량 단위) / 정량·혼합도 숫자
 * - 단위 無 정성   → 자유 텍스트(서술형)
 */
export function fieldInput(
    field: ChecklistField | null,
    item: ChecklistItem
): FieldInput {
    const nameKo = field?.nameKo ?? ''
    if (/\(여부\)/.test(nameKo) || /\(여부\)/.test(item.title)) return 'yesno'

    const unit = (field?.unit ?? '').trim()
    if (unit === '여부') return 'yesno'

    const dataType = (field?.dataType ?? item.dataType ?? '').toLowerCase()
    if (dataType.includes('bool') || dataType.includes('yesno')) return 'yesno'

    if ((unit !== '' && unit !== '여부') || /정량|혼합/.test(dataType)) {
        return 'number'
    }
    return 'text'
}

/** 저장값 → 현재 칩 선택 상태로 정규화. */
export function normalizeChoice(
    answer?: SurveyAnswer
): '예' | '아니오' | '모름' | null {
    if (!answer) return null
    if (answer.unknown) return '모름'
    const v = (answer.value ?? '').trim()
    if (!v) return null
    const up = v.toUpperCase()
    if (up === 'TRUE' || v === '예') return '예'
    if (up === 'FALSE' || v === '아니오') return '아니오'
    if (v === '모름/해당없음') return '모름'
    return null
}

export type Row = {
    key: string
    label: string
    question?: string
    /** 답변 컨트롤 유형(예/아니오·숫자·텍스트). */
    input: FieldInput
    unit?: string
}

export function itemRows(item: ChecklistItem): Row[] {
    if (item.fields.length === 0) {
        return [
            {
                key: itemFieldKey(item),
                label: '항목 답변',
                question: item.title,
                input: fieldInput(null, item),
            },
        ]
    }
    return item.fields.map((field) => ({
        key: field.fieldId,
        label: field.nameKo,
        question: field.question,
        input: fieldInput(field, item),
        unit: field.unit,
    }))
}

export function itemCategoryPath(item: ChecklistItem): string {
    return [item.part, item.majorCategory, item.middleCategory]
        .filter(Boolean)
        .join(' · ')
}
